import React, { useEffect, useRef } from 'react';
import { Alert, Box, Chip, Paper, Skeleton, Typography } from '@mui/material';
import { Lock as LockIcon, NotificationsOff as NotificationsOffIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useDemoStatusQuery } from '../api/JsdhNotifikaceService/service/FeedbackMutations';
import { useAppConfig } from '../api/JsdhNotifikaceService/service/AdminMutations';
import {
    useKanalJednotky,
    useHistorieZprav,
    useOdeslatZpravuMutation,
    ZPRAVY_POLL_SE_SOCKETEM_MS,
} from '../api/JsdhNotifikaceService/service/ChatMutations';
import { useChatSocket } from '../hooks/useChatSocket';
import SeznamZprav from '../components/chat/SeznamZprav';
import ZpravaVstup from '../components/chat/ZpravaVstup';

/**
 * HO-147 Task 4: stranka kanalu jednotky na webu. Dostupna kazdemu clenovi
 * (ne jen adminovi) pod `/chat`, ZA prihlasenim (`ProtectedRoute`), ale
 * BEZ `requireFeature` - historie musi zustat dosazitelna i pri vypnutem
 * `MODUL_CHAT` (viz task-4-brief).
 *
 * Cteni (kanal-jednotky, historie zprav) neni gatovane feature flagem ani
 * demo rezimem - shodne s BE kontraktem (`ChatController`/`KonverzaceController`
 * - "cteni, export, kurzory a moderace jedou vzdy"). Psani JE tvorba obsahu,
 * takze ho gatujeme obema: `MODUL_CHAT` a demo (demo ucet chat videt smi,
 * psat ne).
 */
/**
 * Skeleton kopiruje TVAR vysledku - stridave vlevo a vpravo, ruzne siroke bubliny
 * (nas navrh chatu, sekce 5: "Zadne 'Nacitam...'"). Textova hlaska by uzivateli
 * nerekla nic o tom, co prijde, a pri rychlem nacteni jen problikne.
 */
const SkeletonZprav: React.FC = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }} data-testid="skeleton-zprav">
        {[
            { vlastni: false, sirka: '58%' },
            { vlastni: true, sirka: '42%' },
            { vlastni: false, sirka: '66%' },
            { vlastni: true, sirka: '35%' },
        ].map((r, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: r.vlastni ? 'row-reverse' : 'row', gap: 1 }}>
                {!r.vlastni && <Skeleton variant="circular" width={32} height={32} />}
                <Skeleton variant="rounded" width={r.sirka} height={52} />
            </Box>
        ))}
    </Box>
);

/**
 * Vic pisicich se neshromazduje do vycty jmen - pri zasahu jich muze byt deset
 * a radek by pretekl. Dve jmena jeste nesou informaci "kdo", vic uz jen "kolik".
 */
const popisPisicich = (jmena: string[]): string => {
    if (jmena.length === 1) {
        return `${jmena[0]} píše...`;
    }
    if (jmena.length === 2) {
        return `${jmena[0]} a ${jmena[1]} píší...`;
    }
    // Ceska cislovka se meni po ctyrce: "3 lide pisi", ale "5 lidi pise".
    return jmena.length <= 4
        ? `${jmena.length} lidé píší...`
        : `${jmena.length} lidí píše...`;
};

const ChatPage: React.FC = () => {
    const { user } = useAuth();
    const { enabled: chatEnabled } = useFeatureFlag('MODUL_CHAT');
    const { data: demoStatus } = useDemoStatusQuery();
    const demoEnabled = demoStatus?.demoEnabled === true;

    const { data: appConfig } = useAppConfig();
    const kanalQuery = useKanalJednotky();
    const konverzaceId = kanalQuery.data?.konverzaceId;

    // HO-152: zive spojeni. Dokud drzi, chodi novinky ramcem ZMENA a dotazovani
    // se zpomali na zachrannou sit; kdyz spadne (proxy bez Upgrade, vypadek),
    // stav se prepne na POLLING a rychle kolo se vrati. Stranka tim funguje
    // stejne v obou svetech, jen v jednom rychleji.
    const { stav: stavSpojeni, pisici, oznamPsani } = useChatSocket(konverzaceId);
    const socketZije = stavSpojeni === 'SOCKET';

    const zpravyQuery = useHistorieZprav(konverzaceId, socketZije ? ZPRAVY_POLL_SE_SOCKETEM_MS : undefined);
    const odeslatMutation = useOdeslatZpravuMutation(konverzaceId);

    // konverzaceId musi byt znamy - bez nej by odeslani skoncilo jen chybovym
    // toastem (kanal jeste nedorazil z prvniho GETu, ktery ho insert-or-get zalozi).
    //
    // `ulozisteReady` je soucast podminky, protoze BE vola `gate.overUloziste()`
    // I PRO CISTE TEXTOVOU zpravu (ChatGate: "dokud jednotka nema ulozene evidencni
    // cislo, modul se neaktivuje VUBEC") a odpovida 409. Bez teto podminky web
    // nabidl psaci pole jednotce, ktere backend kazdy zapis odmitne - a hlaska te
    // 409 je psana pro ADMINA ("zkontrolujte evidencni cislo v nastaveni"), takze
    // ji radovy clen dostal jako nesrozumitelnou chybu. `DetailUdalostiPage` i
    // mobil na `ulozisteReady` gatuji, tahle stranka jako jedina ne.
    const ulozisteHotove = appConfig?.ulozisteReady === true;
    const nacitaSe = kanalQuery.isLoading || (!!konverzaceId && zpravyQuery.isLoading);
    const chybaNacteni = kanalQuery.isError || zpravyQuery.isError;

    // PRICHYCENI KE DNU. Chat se otviral odscrollovany na NEJSTARSI zpravu, takze
    // uzivatel videl vrsek poslednich padesati - na vyjezdovem tabletu behem zasahu
    // snadno cetl stare zpravy v domneni, ze jsou aktualni. A kdyz behem cteni
    // dorazila nova (poll), nic ho na ni neupozornilo.
    //
    // Scrolluje se jen kdyz uzivatel UZ je u dna (tolerance 120 px). Kdyz si listuje
    // historii, nova zprava mu pohled neucukne.
    const oblastZprav = useRef<HTMLDivElement | null>(null);
    const pocetZprav = zpravyQuery.data?.zpravy?.length ?? 0;
    useEffect(() => {
        const el = oblastZprav.current;
        if (!el) return;
        const uDna = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        // Prvni vykresleni (scrollTop === 0) se chova jako "u dna" - chat ma
        // zacinat u nejnovejsi zpravy.
        if (uDna || el.scrollTop === 0) {
            el.scrollTop = el.scrollHeight;
        }
    }, [pocetZprav]);
    const muzeZapisovat = chatEnabled && !demoEnabled && !!konverzaceId && ulozisteHotove;
    const zamceno = kanalQuery.data?.zamek === 'UZAMCENO';
    // Ztlumeni SE zobrazuje, ale klient ho nevyhodnocuje (server uz push
    // odfiltroval) - viz "Hotovo, kdyz" v planu HO-147.
    const ztlumeno = kanalQuery.data?.ztlumeno === true;

    return (
        <Box sx={{ maxWidth: 720, mx: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h4" component="h1">
                    Chat jednotky
                </Typography>
                {ztlumeno && (
                    <Chip size="small" icon={<NotificationsOffIcon />} label="Ztlumeno" />
                )}
                {zamceno && (
                    <Chip size="small" icon={<LockIcon />} label="Uzamčeno" />
                )}
            </Box>

            {demoEnabled ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Demo režim — chat jde jen číst, psaní zpráv je vypnuté.
                </Alert>
            ) : !chatEnabled ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Psaní zpráv je momentálně vypnuté. Historie zůstává dostupná ke čtení.
                </Alert>
            ) : !ulozisteHotove ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Chat se ještě nedokončil nastavovat. Historie zůstává dostupná ke čtení;
                    psaní se zapne, jakmile bude jednotka připravená.
                </Alert>
            ) : null}

            <Paper
                ref={oblastZprav}
                variant="outlined"
                sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 320 }}
            >
                {nacitaSe ? (
                    <SkeletonZprav />
                ) : chybaNacteni ? (
                    // Bez tohohle vypadal vypadek site nebo restart backendu presne jako
                    // prazdny kanal - "Zatim zadne zpravy" bez jakekoli hlasky. Uzivatel
                    // nemel jak poznat, ze zpravy existuji a jen nedosly.
                    <Alert severity="error" sx={{ m: 2 }}>
                        Zprávy se nepodařilo načíst. Zkontrolujte připojení — stránka to
                        zkusí sama znovu.
                    </Alert>
                ) : (
                    <SeznamZprav
                        zpravy={zpravyQuery.data?.zpravy ?? []}
                        aktualniUzivatelId={user?.id ?? null}
                        muzeZapisovat={muzeZapisovat}
                    />
                )}
            </Paper>

            {/* Indikator psani ma VYHRAZENOU vysku i kdyz nikdo nepise - jinak
                kazde "pise" o radek posunulo vstupni pole a velitel psal do
                skacejiciho rozhrani. */}
            <Box sx={{ minHeight: 20, px: 0.5, pt: 0.5 }}>
                {pisici.length > 0 && (
                    <Typography variant="caption" color="text.secondary" data-testid="indikator-psani">
                        {popisPisicich(pisici)}
                    </Typography>
                )}
            </Box>

            {muzeZapisovat && (
                <ZpravaVstup
                    onOdeslat={(text) => odeslatMutation.mutateAsync(text)}
                    onPisu={oznamPsani}
                    odesilaSe={odeslatMutation.isPending}
                />
            )}
        </Box>
    );
};

export default ChatPage;
